<template>
  <div class="w-full">
    <lokal-loading
      v-if="isLoading"
      :loading="isLoading"
      size="xs"
    />
    <div v-else-if="maker">
      <p :class="textClasses">
        <span class="font-medium">{{ maker.name }}</span>
        <span class="mx-1">-</span>
        <template v-if="minimumPurchaseAmount === 0">
          <span>mínimo alcanzado</span>
        </template>
        <template v-else>
          <span>{{ useFormatCurrency(minimumPurchaseAmount) }} min</span>
          <span
            v-if="filledBar < 100"
            class="mx-1"
          >
            ·
          </span>
          <span v-if="filledBar < 100">{{ useFormatCurrency(amountToReachedMinimum) }} faltantes</span>
          <span v-else> · alcanzado </span>
        </template>
      </p>
      <div :class="containerClasses">
        <div :class="barBackgroundClasses">
          <div
            :class="barFillClasses"
            :style="{ width: filledBar + '%' }"
          />
        </div>
        <CheckCircleIcon
          class="size-5"
          :class="{ 'text-lk-green': filledBar === 100, 'text-gray-300': filledBar !== 100 }"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { CheckCircleIcon } from '@heroicons/vue/24/solid';
import useFormatCurrency from '../use/format-currency';
import useTotalItemPriceMethods from '../use/total-item-price-methods';
import useMakerDestinationsQuery from '../queries/maker-destinations-query';
import useMakerQuery from '../queries/maker-query';

const TOTAL_PERCENTAGE = 100;

interface MinimumPurchaseProgressBarProps {
  makerId: number;
  items: Item[];
  textClasses?: string;
  containerClasses?: string;
  barBackgroundClasses?: string;
  barFillClasses?: string;
}

const props = withDefaults(defineProps<MinimumPurchaseProgressBarProps>(), {
  textClasses: 'mb-2 text-sm text-gray-600',
  containerClasses: 'flex items-center space-x-2',
  barBackgroundClasses: 'h-2 flex-1 rounded-full bg-gray-200',
  barFillClasses: 'h-full rounded-full bg-lk-green transition-all duration-300',
});

const { maker, makerQuery } = useMakerQuery(computed(() => props.makerId));
const isLoading = computed(() => makerQuery.isLoading.value);

const { currentMakerDestination } = useMakerDestinationsQuery(computed(() => props.makerId));
const minimumPurchaseAmount = computed(
  () => currentMakerDestination.value?.minimumPurchaseAmountCents || maker.value?.minimumPurchaseAmountCents || 0,
);

const { totalProductPriceForItems } = useTotalItemPriceMethods();

const filledBar = computed(() => {
  if (!minimumPurchaseAmount.value || minimumPurchaseAmount.value === 0) return TOTAL_PERCENTAGE;

  const totalForMaker = totalProductPriceForItems(props.items);
  if (totalForMaker >= minimumPurchaseAmount.value) {
    return TOTAL_PERCENTAGE;
  }
  return (totalForMaker * TOTAL_PERCENTAGE) / minimumPurchaseAmount.value;
});

const amountToReachedMinimum = computed(() => {
  if (!minimumPurchaseAmount.value || minimumPurchaseAmount.value === 0) return 0;

  const totalForMaker = totalProductPriceForItems(props.items);
  const diff = minimumPurchaseAmount.value - totalForMaker;
  return diff > 0 ? diff : 0;
});
</script>
